/**
 * Соответствие legacy ↔ новых URL кабинета (ТЗ SEO-8 §5.1, таблица §4.1).
 *
 * Чистый модуль без Angular-зависимостей: его зовут мидлварь `server.ts`,
 * предзагрузочная перезапись в `main.ts`, guard на `**`, проверка доступа перед
 * `POST /ch`, сайдбар и sitemap-билдер (ТЗ §5.1, шесть потребителей).
 *
 * **Правила префиксные, а не поштучные.** Часть пунктов меню (`orders/favorites`,
 * `private/legal`, `reports/products`, …) фронтовых роутов в этом проекте не имеет —
 * общий jar обслуживает много фронтов. Поштучная карта оставила бы их legacy.
 */

/** Разобранный URL: то, что даёт и `location`, и разбор строки роутером. */
export interface UrlParts {
  pathname: string;
  /** С ведущим `?` либо пустая строка — как у `location.search`. */
  search: string;
  /** С ведущим `#` либо пустая строка — как у `location.hash`. */
  hash: string;
}

/**
 * Разбор `path[?query][#hash]`. Единственный разборщик URL в этом слое: раньше
 * идентичный шестистрочный блок был скопирован в `toLegacyUrl`,
 * `resolveLegacyRedirect`, `slug-canonical.ts` и `canonical-redirect.ts`.
 *
 * Существующие хелперы не годятся: `normalizePath` (`redirect-matcher.ts`) и
 * `pathOnly` (`sitemap/url-pattern.ts`) отдают только путь и добавляют свою
 * семантику (срез хвостового слэша / хоста).
 */
export function splitUrl(url: string): UrlParts {
  const hashAt = url.indexOf('#');
  const hash = hashAt >= 0 ? url.slice(hashAt) : '';
  const withoutHash = hashAt >= 0 ? url.slice(0, hashAt) : url;
  const queryAt = withoutHash.indexOf('?');
  return {
    pathname: queryAt >= 0 ? withoutHash.slice(0, queryAt) : withoutHash,
    search: queryAt >= 0 ? withoutHash.slice(queryAt) : '',
    hash,
  };
}

/** Результат: путь + добавки в query для правил с захватом значения. */
export interface NewPath {
  path: string;
  addQuery?: Record<string, string>;
}

/**
 * Правила с захватом значения (path → query). Проверяются **до** префиксных:
 * иначе `/cabinet/news/common/page/3` ушёл бы в `/news/page/3` вместо `/news?page=3`.
 *
 * Это не только совместимость, но и починка: path-пагинация в проекте нерабочая —
 * `:page` не читает никто, и такой URL отдавал первую страницу под SSR с
 * самоканоникалом, то есть индексируемый дубль (ТЗ §6.9).
 */
const CAPTURE_RULES: readonly {readonly re: RegExp; readonly path: string; readonly param: string}[] = [
  {re: /^\/cabinet\/news\/common\/page\/(\d+)\/?$/, path: '/news', param: 'page'},
  {re: /^\/ad-campaigns\/page\/(\d+)\/?$/, path: '/promotions', param: 'page'},
];

/**
 * Префиксные правила, **сверху вниз, первое совпадение** (таблица ТЗ §4.1).
 * Порядок — часть контракта, не косметика:
 *   * `/cabinet/news/common` и `/cabinet/news/client` до `/cabinet/news`;
 *   * `/cabinet/private/account` и `/cabinet/private/company` до `/cabinet/private`;
 *   * общее `/cabinet/` — предпоследним, `/cabinet` (ровно) — последним, иначе оно
 *     перехватило бы вообще всё через match «префикс + /».
 */
const PREFIX_RULES: readonly (readonly [string, string])[] = [
  ['/cabinet/dashboard', '/'],
  ['/cabinet/news/common', '/news'],
  ['/cabinet/news/client', '/account/news'],
  ['/cabinet/news', '/news'],
  ['/cabinet/pages', '/pages'],
  ['/cabinet/faq', '/faq'],
  ['/cabinet/private/account', '/account/profile'],
  ['/cabinet/private/company', '/account/company'],
  ['/cabinet/private', '/account'],
  ['/cabinet/notifications', '/account/notifications'],
  ['/cabinet/sign-in', '/sign-in'],
  ['/cabinet/sign-up', '/sign-up'],
  ['/cabinet/reset', '/reset'],
  ['/ad-campaigns', '/promotions'],
  ['/cabinet/', '/account/'],
  ['/cabinet', '/'],
];

/**
 * Legacy-путь → новый. Идемпотентна: применение к результату ничего не меняет
 * (обязательное свойство — `/catalog` в меню уже без `/cabinet`, а мидлварь и
 * предзагрузочная перезапись могут сработать по одному URL обе).
 *
 * @returns путь и, для правил с захватом, добавки в query. Мержит их вызывающий:
 *          при конфликте побеждает параметр, уже присутствующий в URL (ТЗ §5.1).
 */
export function toNewPath(path: string): NewPath {
  const normalized = normalizeSlashes(path);
  for (const rule of CAPTURE_RULES) {
    const match = rule.re.exec(normalized);
    if (match) {
      return {path: rule.path, addQuery: {[rule.param]: match[1]}};
    }
  }
  return {path: applyPrefixRules(normalized, PREFIX_RULES) ?? normalized};
}

/**
 * Нормализация слэшей ДО правил — и дублей, и хвостового. Оба случая найдены
 * браузерной проверкой, и оба про то, что правила префиксные:
 *
 *   * `/cabinet/` без нормализации не попадал в правило корня (`/cabinet` → `/`) и
 *     уезжал в общее `/cabinet/` → `/account/`: старая закладка на корень кабинета
 *     приводила в ПРОФИЛЬ вместо главной. Побочно исчезает лишний хоп у
 *     `/cabinet/news/common/` — было `/news/`, чей canonical всё равно `/news`;
 *   * `/cabinet//news/common/9` проходил то же общее правило и давал
 *     `/account//news/common/9`. В SSR это ловила серверная мидлварь, а в CSR-сборке
 *     (сервера нет) пользователь оказывался в профиле вместо статьи. Схлопывание
 *     здесь, в общей карте, выравнивает оба режима — ровно то, ради чего §6.10
 *     держит один набор правил на три слоя.
 *
 * Корень сайта не трогаем: у него слэш — единственный символ.
 */
function normalizeSlashes(path: string): string {
  const collapsed = path.replace(/\/{2,}/g, '/');
  return collapsed.length > 1 && collapsed.endsWith('/') ? collapsed.replace(/\/+$/, '') : collapsed;
}

/**
 * Общий проход по таблице префиксных правил — один для обоих направлений.
 * Раньше цикл был скопирован в `toNewPath` и `toLegacyPath`, из-за чего правка
 * нормализации хвостового слэша в одном направлении не доезжала до другого.
 *
 * @returns новый путь либо `null`, если ни одно правило не совпало
 */
function applyPrefixRules(path: string, rules: readonly (readonly [string, string])[]): string | null {
  for (const [from, to] of rules) {
    const tail = matchPrefix(path, from);
    if (tail === null) {
      continue;
    }
    // `matchPrefix` отдаёт хвост С ведущим слэшем, поэтому у базы хвостовой слэш
    // снимаем — иначе правило `/cabinet/` → `/account/` даёт `/account//orders`.
    const base = to.length > 1 && to.endsWith('/') ? to.slice(0, -1) : to;
    // База `/` с непустым хвостом дала бы `//x`: корень уже несёт слэш.
    return base === '/' ? tail || '/' : base + tail;
  }
  return null;
}

/**
 * Обратные правила — **только** для `POST /ch` (ТЗ §3.3).
 *
 * Бэк проверяет `strpos(:url, menu.uri) > 0`, то есть значения `menu.uri` в БД
 * остаются контрактом для фронтовых путей, и перед проверкой доступа новый путь
 * надо вернуть в legacy-форму.
 *
 * `/account/legal` и `/account/managers` перечислены явно, хотя общее правило
 * `/account/` → `/cabinet/` их бы тоже поймало: оно дало бы `/cabinet/legal`, потеряв
 * сегмент `private`, и `strpos` для `menu.uri = /cabinet/private/legal` вернул бы 0.
 * Фронтовых роутов у них сейчас нет, поэтому вживую это не стреляет, — но добавить
 * роут и молча потерять доступ было бы легко.
 */
const LEGACY_RULES: readonly (readonly [string, string])[] = [
  ['/account/profile', '/cabinet/private/account'],
  ['/account/company', '/cabinet/private/company'],
  ['/account/legal', '/cabinet/private/legal'],
  ['/account/managers', '/cabinet/private/managers'],
  ['/account/news', '/cabinet/news/client'],
  ['/account/notifications', '/cabinet/notifications'],
  ['/account/', '/cabinet/'],
  ['/account', '/cabinet/private'],
  ['/news', '/cabinet/news/common'],
  ['/promotions', '/ad-campaigns'],
  ['/pages', '/cabinet/pages'],
  ['/faq', '/cabinet/faq'],
  ['/sign-in', '/cabinet/sign-in'],
  ['/sign-up', '/cabinet/sign-up'],
  ['/reset', '/cabinet/reset'],
];

/**
 * Новый путь → legacy. Единственный потребитель — `auth.guard.menu.service.ts`
 * перед `POST /ch`. Для навигации, ссылок и canonical это использовать нельзя.
 */
export function toLegacyPath(path: string): string {
  // Корень — точный случай, а не префиксное правило: как префикс `/` поймал бы
  // вообще всё и превратил `/account/chat/standard` в
  // `/cabinet/dashboard/account/chat/standard`.
  if (path === '/') {
    return '/cabinet/dashboard';
  }

  return applyPrefixRules(path, LEGACY_RULES) ?? path;
}

/**
 * Полный URL навигации → legacy-форма (для `POST /ch`).
 *
 * Обёртка над `toLegacyPath`, разбирающая query и hash. Нужна потому, что
 * `AuthGuardMenuService` передаёт `RouterStateSnapshot.url`, то есть путь ВМЕСТЕ с
 * query, а префиксные правила работают по чистому пути.
 *
 * Без неё `/news?page=2` не матчил ни одного правила и уезжал в `/ch` нетронутым:
 * бэк не находил в нём `menu.uri`, и ЛЮБАЯ страница под menu-guard с
 * query-параметрами отдавала 404. Поймано в браузере — curl по «голым» путям это
 * не показывал.
 *
 * Query и hash сохраняются: бэку они безразличны (`strpos` ищет подстроку), а
 * терять информацию по дороге незачем.
 */
export function toLegacyUrl(url: string): string {
  const {pathname, search, hash} = splitUrl(url);
  return `${toLegacyPath(pathname)}${search}${hash}`;
}

/**
 * Нужен ли редирект для полного URL навигации и куда (слой C, §6.10).
 *
 * Живёт здесь, а не в файле guard'а, намеренно: спека должна импортировать логику
 * БЕЗ Angular. Guard тянет `@angular/core`/`@angular/router`, а первая версия ещё
 * и `environment` — тот, в свою очередь, barrel `compob2b-libs`, и node-env спека
 * падала на загрузке (`Class extends value undefined`, ловушка из CLAUDE.md).
 *
 * @returns новый URL либо `null`, если путь не legacy — тогда рендерим NotFound.
 *          Возвращать редирект «на всякий случай» нельзя: `**` перестал бы
 *          показывать 404 на настоящих битых ссылках, и монитор битых ссылок
 *          (`report404`) ослеп бы.
 */
export function resolveLegacyRedirect(url: string): string | null {
  const next = toNewUrl(splitUrl(url));
  return next === url ? null : next;
}


/**
 * Собирает новый URL целиком: путь через `toNewPath`, query с мержем `addQuery`,
 * hash как есть.
 *
 * Общая для двух потребителей — предзагрузочной перезаписи (слой A) и guard'а на
 * `**` (слой C). Раньше эта сборка жила только в первом; вынесена, чтобы у слоёв
 * не разъехались правила мержа query и обработка hash.
 */
export function toNewUrl({pathname, search, hash}: UrlParts): string {
  const fromQuery = catalogIdFromQuery(toNewPath(pathname).path, search);
  const {path, addQuery} = fromQuery ?? toNewPath(pathname);
  const carried = fromQuery ? fromQuery.search : search;

  // Query переносим ДОСЛОВНО. Прогонять его через `URLSearchParams.toString()`
  // нельзя: тот нормализует (пробел → `+`, параметр без значения → `key=`), а
  // решение о редиректе принимается сравнением строк — то есть НЕ-legacy URL с
  // пробелом в значении считался бы изменившимся и уезжал в 301.
  //
  // Это ловилось в браузере как БЕСКОНЕЧНАЯ петля: мидлварь отдавала 301 с
  // `/catalog?keyword=a+b`, Angular своим сериализатором (он пишет пробел как
  // `%20`) отвечал 302 назад на `%20`-форму, и так по кругу — на любом поиске из
  // двух слов. Спека фиксирует инвариант «путь не legacy → URL байт-в-байт».
  const additions = addQuery ? missingParams(carried, addQuery) : '';
  // `carried` уже несёт ведущий `?`, если непустой — поэтому дописываем либо через `&`
  // к готовой строке, либо начинаем новую с `?`.
  let query = carried;
  if (additions) {
    query = carried ? `${carried}&${additions}` : `?${additions}`;
  }

  // hash обязателен в сборке: и `replaceState`, и навигация роутера заменяют URL
  // целиком, поэтому без него якорь просто потерялся бы.
  return `${path}${query}${hash}`;
}

/** Результат перевода query-формы категории в path-форму. */
interface CatalogFromQuery extends NewPath {
  /** Остаток query (с ведущим `?`) уже без `catalog_id`. */
  search: string;
}

/**
 * Старая форма категории: `/catalog?catalog_id=N` → `/catalog/N` (ТЗ §7).
 *
 * До переезда категория адресовалась именно так — такие URL уходили в sitemap
 * (`catalog-tree.select.ts` до правки) и потому лежат в индексе. Без этого правила они
 * отдают 200 и рендерят КОРЕНЬ каталога: идентификатор из query больше никто не читает
 * (он уехал в path), а canonical ссылается сам на себя — то есть весь
 * проиндексированный корпус категорий показывал бы не тот контент и плодил дубли.
 * Найдено браузерной проверкой.
 *
 * Живёт в `toNewUrl`, а не в `toNewPath`: правило смотрит на query, а `toNewPath` о нём
 * не знает по определению. Работает во всех трёх слоях §6.10 сразу, потому что все они
 * ходят через `toNewUrl`/`resolveLegacyRedirect`.
 *
 * @returns `null`, если правило не применимо (чужой путь, нечисловой или пустой id)
 */
function catalogIdFromQuery(path: string, search: string): CatalogFromQuery | null {
  if (path !== '/catalog' || !search) {
    return null;
  }
  const params = new URLSearchParams(search);
  const id = params.get('catalog_id');
  // Ключ категории — только число: мусорное значение не должно порождать `/catalog/abc`.
  if (!id || !/^\d+$/.test(id)) {
    return null;
  }
  params.delete('catalog_id');
  // Остальные параметры (region, limit, фильтры, page) переносим ДОСЛОВНО в исходном
  // порядке: `URLSearchParams.toString()` нормализует пробелы в `+`, а этого делать
  // нельзя — на том уже ловили петлю 301↔302 (см. комментарий в `toNewUrl`).
  const rest = search.replace(/^\?/, '')
    .split('&')
    .filter(pair => pair !== '' && !pair.startsWith('catalog_id='))
    .join('&');
  return {path: `/catalog/${id}`, search: rest ? `?${rest}` : ''};
}

/**
 * Параметры правила с захватом, которых в query ещё нет — уже в готовой для
 * склейки форме (`a=1&b=2`).
 *
 * При конфликте побеждает параметр, уже присутствующий в URL (ТЗ §5.1):
 * `/cabinet/news/common/page/3?page=7` → `?page=7`. Для ПРОВЕРКИ наличия разбор
 * через `URLSearchParams` безопасен — наружу его сериализация не уходит.
 */
function missingParams(search: string, addQuery: Record<string, string>): string {
  const existing = new URLSearchParams(search);
  return Object.entries(addQuery)
    .filter(([key]) => !existing.has(key))
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
}

/** Минимум, который нужен от пункта меню: ссылка и вложенные пункты. */
export interface MenuLinkNode {
  link?: string;
  children?: MenuLinkNode[];
}

/**
 * Переписывает `link` у пунктов меню рекурсивно, **возвращая новые объекты**.
 *
 * Вызывать обязан **каждый** потребитель `/menu` в кабинете: меню рендерится
 * напрямую из `item.link`, поэтому без этого сайдбар остался бы на legacy-ссылках
 * и каждый клик шёл бы через 301.
 *
 * Вход не мутируется осознанно: `StoreService.getMenu()` — общий метод
 * `compob2b-libs`, который зовут ОБА приложения, и мутация протекла бы в админский
 * сайдбар, где `/cabinet/pim/brand` превратился бы в `/account/pim/brand` (ТЗ §11.1).
 *
 * `addQuery` здесь игнорируется: правила с захватом относятся к пагинации, в
 * `menu.uri` таких значений не бывает.
 */
export function rewriteMenuLinks<T extends MenuLinkNode>(items: readonly T[]): T[] {
  return items.map(item => {
    const rewritten: T = {...item};
    if (item.link) {
      rewritten.link = toNewPath(item.link).path;
    }
    if (item.children) {
      rewritten.children = rewriteMenuLinks(item.children);
    }
    return rewritten;
  });
}

/**
 * Совпадение префикса. Правило со слэшем на конце (`/cabinet/`) — это «всё, что
 * глубже»; без слэша — либо точное совпадение, либо префикс по границе сегмента,
 * чтобы `/cabinet/newsletter` не поймалось правилом `/cabinet/news`.
 *
 * @returns хвост после префикса (может быть пустым) либо `null`, если не совпало
 */
function matchPrefix(path: string, from: string): string | null {
  if (from.endsWith('/')) {
    return path.startsWith(from) ? path.slice(from.length - 1) : null;
  }
  if (path === from) {
    return '';
  }
  return path.startsWith(`${from}/`) ? path.slice(from.length) : null;
}
